<template>
  <aside
    id="app-sidebar"
    ref="sidebarRef"
    class="sidebar-root"
    :class="{
      'sidebar-root--pinned-collapsed': isCollapsed && !isMobile,
      'sidebar-root--rail-layout': isRailLayout,
      'sidebar-root--hover-expanded': isHoverExpanded,
      'sidebar-root--mobile-open': isMobile && isSidebarVisible,
      'sidebar-root--mobile-hidden': isMobile && !isSidebarVisible
    }"
    aria-label="Primary navigation"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click.stop
  >
    <div class="sidebar-root__header">
      <button
        type="button"
        class="sidebar-brand"
        aria-label="Go to my profile"
        @click="goToMyProfile"
      >
        <span class="sidebar-brand__mark">{{ shellHeader.brandMark }}</span>
        <span class="sidebar-brand__copy">
          <span class="sidebar-brand__title">{{ shellHeader.title }}</span>
          <span class="sidebar-brand__subtitle">{{ shellHeader.subtitle }}</span>
        </span>
      </button>

      <button
        v-if="!isMobile"
        type="button"
        class="sidebar-collapse-btn"
        :class="{ 'sidebar-collapse-btn--active': collapseAriaExpanded }"
        :aria-expanded="collapseAriaExpanded ? 'true' : 'false'"
        aria-controls="app-sidebar"
        :aria-label="collapseAriaExpanded ? 'Collapse navigation' : 'Expand navigation'"
        v-tooltip.right="collapseAriaExpanded ? 'Collapse navigation' : 'Expand navigation'"
        @click="toggleCollapsed"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
          :class="{ 'sidebar-collapse-btn__icon--collapsed': isRailMode }"
        >
          <path
            d="M15 18l-6-6 6-6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div class="sidebar-root__scroll">
      <SidebarNavSection
        v-for="section in navSectionGroups"
        :key="section.id"
        :section="section"
        :is-rail-mode="isRailMode"
        @navigate="navigateMenuItem"
      />
    </div>

    <div class="sidebar-root__footer">
      <SidebarUserCard
        v-if="accountMenuActions.length"
        :user-card="userCard"
        :account-menu-actions="accountMenuActions"
        :show-sidebar-behavior-controls="showSidebarBehaviorControls"
        :hover-expand-enabled="hoverExpandEnabled"
        :is-rail-mode="isRailMode"
        @menu-open-change="setUserMenuOpen"
        @toggle-hover-expand="toggleHoverExpand"
        @logout="logout"
      />
    </div>
  </aside>

  <button
    v-if="isMobile"
    type="button"
    class="sidebar-mobile-toggle"
    :aria-label="isSidebarVisible ? 'Hide navigation' : 'Show navigation'"
    @click.stop="toggleSidebar"
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
    </svg>
  </button>

  <div
    v-if="isMobile"
    class="sidebar-scrim"
    :class="{ 'sidebar-scrim--visible': isSidebarVisible }"
    aria-hidden="true"
    @click="handleOverlayClick"
  />
</template>

<script>
import { ref, inject, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { SIDEBAR_LAYOUT_KEY } from '@/features/hr-workspace/constants/sidebarLayout';
import { shellHeader } from '@/features/hr-workspace/data/shellConfig';
import { useSidebarNav } from '@/features/hr-workspace/composables/useSidebarNav';
import { isRouteActive } from '@/features/hr-workspace/utils/shellNavActive';
import SidebarNavSection from '@/components/sidebar/SidebarNavSection.vue';
import SidebarUserCard from '@/components/sidebar/SidebarUserCard.vue';
import { useSidebarNavMotion } from '@/components/sidebar/useSidebarNavMotion';
import { useSidebarCollapseMotion } from '@/components/sidebar/useSidebarCollapseMotion';
import { useSidebarScrollMotion } from '@/components/sidebar/useSidebarScrollMotion';

export default {
  name: 'Sidebar',
  components: {
    SidebarNavSection,
    SidebarUserCard
  },
  props: {
    isSidebarVisible: {
      type: Boolean,
      default: true
    }
  },
  emits: ['toggle-sidebar'],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const currentUserId = computed(() => store.state.users?.user?.id ?? null);
    const sidebarLayout = inject(SIDEBAR_LAYOUT_KEY);

    if (!sidebarLayout) {
      throw new Error('Sidebar requires SIDEBAR_LAYOUT_KEY from App.vue');
    }

    const sidebarRef = ref(null);
    const isRailLayout = ref(
      !sidebarLayout.isMobile.value &&
        sidebarLayout.isCollapsed.value &&
        !sidebarLayout.isHoverExpanded.value
    );

    const { playNavIconPulse } = useSidebarNavMotion(sidebarRef);

    useSidebarCollapseMotion(sidebarRef, {
      isCollapsed: sidebarLayout.isCollapsed,
      isHoverExpanded: sidebarLayout.isHoverExpanded,
      isMobile: sidebarLayout.isMobile,
      isRailLayout
    });

    useSidebarScrollMotion(sidebarRef);

    const {
      navSectionGroups,
      userCard,
      accountMenuActions,
      showSidebarBehaviorControls,
      goToMyProfile,
      logout
    } = useSidebarNav();

    const navigateMenuItem = (item, event) => {
      if (!item?.path) return;
      event?.preventDefault();
      playNavIconPulse(item.path);

      if (item.key === 'my-profile') {
        if (route.name === 'my-profile') {
          if (sidebarLayout.isMobile.value) {
            emit('toggle-sidebar');
          }
          return;
        }
        router.push({ name: 'my-profile' }).catch(() => {});
        if (sidebarLayout.isMobile.value) {
          emit('toggle-sidebar');
        }
        return;
      }

      const alreadyActive = isRouteActive(item.path, route.path, {
        routeName: item.routeName,
        activeRouteName: route.name,
        relatedRouteNames: item.relatedRouteNames,
        routeParams: route.params,
        currentUserId: currentUserId.value,
        navKey: item.key
      });

      if (alreadyActive) {
        if (sidebarLayout.isMobile.value) {
          emit('toggle-sidebar');
        }
        return;
      }

      router.push(item.path).catch(() => {});
      if (sidebarLayout.isMobile.value) {
        emit('toggle-sidebar');
      }
    };

    const toggleSidebar = () => {
      emit('toggle-sidebar');
    };

    const handleOverlayClick = () => {
      if (sidebarLayout.isMobile.value && props.isSidebarVisible) {
        toggleSidebar();
      }
    };

    return {
      shellHeader,
      sidebarRef,
      navSectionGroups,
      userCard,
      accountMenuActions,
      showSidebarBehaviorControls,
      goToMyProfile,
      logout,
      navigateMenuItem,
      toggleSidebar,
      handleOverlayClick,
      isRailLayout,
      // Explicit layout bindings (avoid spread — HMR can drop keys from instance)
      isCollapsed: sidebarLayout.isCollapsed,
      isHoverExpanded: sidebarLayout.isHoverExpanded,
      isMobile: sidebarLayout.isMobile,
      isRailMode: sidebarLayout.isRailMode,
      collapseAriaExpanded: sidebarLayout.collapseAriaExpanded,
      hoverExpandEnabled: sidebarLayout.hoverExpandEnabled,
      toggleCollapsed: sidebarLayout.toggleCollapsed,
      toggleHoverExpand: sidebarLayout.toggleHoverExpand,
      handleMouseEnter: sidebarLayout.handleMouseEnter,
      handleMouseLeave: sidebarLayout.handleMouseLeave,
      setUserMenuOpen: sidebarLayout.setUserMenuOpen
    };
  }
};
</script>

<style scoped>
.sidebar-root {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 264px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: hidden;
  background: var(--p-toolbar-background);
  border-right: 1px solid var(--crc-sidebar-line);
  color: var(--crc-sidebar-text);
  transition:
    transform var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1)),
    box-shadow var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1));
}

/* Initial paint before GSAP owns width — matches SIDEBAR_WIDTHS.rail */
.sidebar-root--rail-layout {
  width: 76px;
}

/* Width is owned by GSAP — only overlay chrome here. */
.sidebar-root--hover-expanded {
  z-index: 1001;
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.12);
}

.sidebar-root__header {
  position: relative;
  min-height: 4.875rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 3.25rem 0 0.875rem;
  /* Inset shadow avoids border-corner miter with sidebar border-right (1px step). */
  border-bottom: none;
  box-shadow: inset 0 -1px 0 var(--crc-sidebar-line);
  flex-shrink: 0;
  overflow: visible;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.6875rem;
  min-width: 0;
  flex: 1 1 auto;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  text-align: left;
}

.sidebar-brand:focus-visible,
.sidebar-collapse-btn:focus-visible,
.sidebar-mobile-toggle:focus-visible {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 2px;
}

.sidebar-brand__mark {
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 2.25rem;
  display: grid;
  place-items: center;
  border-radius: 0.75rem;
  background: var(--crc-sidebar-brand-mark-bg);
  border: 1px solid var(--crc-sidebar-brand-mark-border);
  color: var(--crc-sidebar-brand-mark-text);
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.sidebar-brand__copy {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 11rem;
  overflow: hidden;
}

.sidebar-brand__title {
  display: block;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-brand__subtitle {
  display: block;
  margin-top: 0.125rem;
  font-size: 0.6875rem;
  line-height: 1.25;
  color: var(--crc-sidebar-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapse-btn {
  position: absolute;
  top: 50%;
  right: 0.875rem;
  z-index: 2;
  width: 2.125rem;
  height: 2.125rem;
  margin: 0;
  border: 1px solid var(--crc-sidebar-line);
  border-radius: 0.625rem;
  background: var(--crc-sidebar-collapse-bg);
  color: var(--crc-sidebar-text-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  transform: translateY(-50%);
  /* Position snaps with rail layout — only color fades here (GSAP owns opacity). */
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-collapse-btn:hover {
  background: var(--crc-sidebar-collapse-hover-bg);
  border-color: var(--crc-sidebar-line-strong);
  color: var(--crc-sidebar-text);
}

.sidebar-collapse-btn--active {
  background: var(--crc-sidebar-collapse-active-bg);
  border-color: var(--crc-sidebar-collapse-active-border);
  color: var(--crc-sidebar-active-icon);
}

.sidebar-collapse-btn--active:hover {
  background: var(--crc-sidebar-collapse-active-hover-bg);
  border-color: var(--crc-sidebar-collapse-active-border);
  color: var(--crc-sidebar-active-icon);
}

.sidebar-collapse-btn svg {
  width: 1rem;
  height: 1rem;
  transition: transform 0.2s ease;
}

.sidebar-collapse-btn__icon--collapsed {
  transform: rotate(180deg);
}

.sidebar-root__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.875rem 0.75rem 1rem 0.75rem;
  /* GSAP tweens width on sidebar hover (useSidebarScrollMotion); thumb color is fixed. */
  --sidebar-scrollbar-width: 0px;
  scrollbar-width: none;
  scrollbar-gutter: stable;
  transition: padding var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1));
}

.sidebar-root__scroll--sb-visible {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.14) transparent;
}

.sidebar-root__scroll::-webkit-scrollbar {
  width: var(--sidebar-scrollbar-width);
  height: 0;
}

.sidebar-root__scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

/* Hide scrollbar during pin motion — avoids abrupt thumb vanish when overflow ends. */
.sidebar-root--motion-lock .sidebar-root__scroll {
  overflow-y: hidden;
  scrollbar-width: none;
  --sidebar-scrollbar-width: 0px !important;
}

.sidebar-root--motion-lock .sidebar-root__scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.sidebar-root__footer {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  padding: 0.625rem;
  /* Same as header: inset line, no miter with border-right. */
  border-top: none;
  box-shadow: inset 0 1px 0 var(--crc-sidebar-line);
  transition: padding var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1));
}

.sidebar-root--rail-layout .sidebar-root__header {
  min-height: 7rem;
  justify-content: flex-start;
  align-items: center;
  padding: 0.75rem var(--sidebar-rail-gutter) 3.25rem;
}

.sidebar-root--rail-layout .sidebar-brand {
  flex: 0 0 auto;
  justify-content: center;
  width: 100%;
  margin-top: 0.25rem;
}

.sidebar-root--rail-layout .sidebar-brand__copy {
  display: none;
}

/* Slide toggle under brand mark — no flex-direction snap. */
.sidebar-root--rail-layout .sidebar-collapse-btn {
  top: auto;
  right: auto;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
}

.sidebar-root--rail-layout .sidebar-root__scroll {
  padding: 0.875rem var(--sidebar-rail-gutter);
}

.sidebar-root--rail-layout .sidebar-root__footer {
  padding: 0.625rem var(--sidebar-rail-gutter);
}

.sidebar-mobile-toggle {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 997;
  width: 2.375rem;
  height: 2.375rem;
  border: 1px solid var(--crc-sidebar-line);
  border-radius: 0.625rem;
  background: var(--p-toolbar-background);
  color: var(--crc-sidebar-text);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.sidebar-mobile-toggle svg {
  width: 1.125rem;
  height: 1.125rem;
}

.sidebar-scrim {
  position: fixed;
  inset: 0;
  z-index: 998;
  background: var(--crc-sidebar-scrim);
  backdrop-filter: blur(2px);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1)),
    visibility var(--sidebar-transition-duration, 360ms) var(--sidebar-transition-easing, cubic-bezier(0.45, 0, 0.55, 1));
}

.sidebar-scrim--visible {
  opacity: 1;
  visibility: visible;
}

.sidebar-root--mobile-hidden {
  transform: translateX(-100%);
}

.sidebar-root--mobile-open {
  transform: translateX(0);
  width: min(86vw, 264px) !important;
  box-shadow: 20px 0 60px rgba(0, 0, 0, 0.5);
}

@media (max-width: 991.98px) {
  .sidebar-collapse-btn {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-root,
  .sidebar-scrim,
  .sidebar-collapse-btn svg {
    transition: none !important;
  }
}
</style>

<style>
.sidebar-nav-section + .sidebar-nav-section {
  margin-top: 0.875rem;
}

.sidebar-root--rail-layout .sidebar-nav-section + .sidebar-nav-section {
  margin-top: 0.5rem;
}

.sidebar-nav-section__label {
  margin: 0 0 0.375rem;
  padding: 0 0.625rem;
  color: var(--crc-sidebar-text-muted);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Explicit line box so GSAP height tweens are stable across browsers. */
  line-height: 1.25;
  box-sizing: border-box;
}

.sidebar-root--rail-layout .sidebar-nav-section__label {
  visibility: hidden;
  height: 0;
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.sidebar-nav-section__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.25rem;
}

.sidebar-nav-link {
  box-sizing: border-box;
  width: 100%;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  gap: 0.6875rem;
  padding: 0 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.6875rem;
  background: transparent;
  color: var(--crc-sidebar-link);
  text-decoration: none;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.sidebar-nav-link:hover {
  color: var(--crc-sidebar-text);
  background: var(--crc-sidebar-link-hover-bg);
  border-color: var(--crc-sidebar-link-hover-border);
}

.sidebar-nav-link:focus-visible {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 1px;
}

.sidebar-nav-link--active {
  position: relative;
  color: var(--crc-sidebar-active-text);
  background: var(--crc-sidebar-active-bg);
  border-color: transparent;
  box-shadow: none;
}

.sidebar-nav-link--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.4375rem;
  bottom: 0.4375rem;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--crc-sidebar-active-icon);
}

.sidebar-nav-link--active .sidebar-nav-link__icon {
  color: var(--crc-sidebar-active-icon);
}

.sidebar-nav-link--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sidebar-nav-link__icon {
  width: 1.125rem;
  height: 1.125rem;
  flex: 0 0 1.125rem;
  display: inline-grid;
  place-items: center;
  color: var(--crc-sidebar-text-muted);
}

.sidebar-nav-link__icon .pi {
  font-size: 1rem;
  display: block;
  line-height: 1;
}

.sidebar-nav-link__icon--has-alert {
  position: relative;
}

.sidebar-nav-link__icon--has-alert::after {
  content: '';
  position: absolute;
  top: -0.0625rem;
  right: -0.0625rem;
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 999px;
  background: var(--crc-danger, var(--crc-sidebar-badge-count-bg));
  box-shadow: 0 0 0 1px var(--crc-sidebar-bg, #fff);
}

.sidebar-nav-link__text {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 12rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8125rem;
  font-weight: 560;
  text-align: left;
}

.sidebar-nav-badge {
  min-width: 1.3125rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.4375rem;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  flex-shrink: 0;
}

.sidebar-nav-badge--count {
  background: var(--crc-sidebar-badge-count-bg);
  color: var(--crc-sidebar-badge-count-text);
}

.sidebar-nav-badge--status {
  background: var(--crc-sidebar-badge-status-bg);
  color: var(--crc-sidebar-badge-status-text);
}

.sidebar-nav-link__rail-badge {
  position: absolute;
  top: 0.1875rem;
  right: 0.1875rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: var(--crc-sidebar-active-icon);
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 800;
  line-height: 1;
  pointer-events: none;
  box-shadow: 0 0 0 1px var(--p-toolbar-background);
}

.sidebar-nav-link--has-rail-badge {
  position: relative;
  overflow: visible;
}

.sidebar-root--rail-layout .sidebar-nav-section__item {
  display: flex;
  justify-content: center;
  overflow: visible;
}

.sidebar-root--rail-layout .sidebar-nav-link {
  position: relative;
  overflow: visible;
  width: var(--sidebar-rail-target-size);
  min-height: var(--sidebar-rail-target-size);
  max-width: var(--sidebar-rail-target-size);
  margin-inline: 0;
  padding: 0;
  gap: 0;
  justify-content: center;
  align-items: center;
}

.sidebar-root--rail-layout .sidebar-nav-link__text,
.sidebar-root--rail-layout .sidebar-nav-badge {
  display: none;
}

/*
 * Expand mid-state: nav/user copy takes no flex space (icons stay left).
 * Brand copy keeps its layout box — only opacity is GSAP-faded — so the HR
 * mark and collapse button do not jump when labels-pending toggles.
 */
.sidebar-root--labels-pending .sidebar-nav-link,
.sidebar-root--labels-pending .sidebar-user-card {
  gap: 0;
}

.sidebar-root--labels-pending .sidebar-nav-link__text,
.sidebar-root--labels-pending .sidebar-nav-badge,
.sidebar-root--labels-pending .sidebar-user-card__copy,
.sidebar-root--labels-pending .sidebar-user-card__menu-icon {
  flex: 0 0 0;
  width: 0;
  max-width: 0;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  pointer-events: none;
}

.sidebar-root--labels-pending .sidebar-brand__copy {
  /* Reserve space; GSAP owns visibility/opacity during the tween. */
  pointer-events: none;
}

/* Section titles: height is GSAP-tweened — do not snap max-height here (icon jump). */

.sidebar-root--rail-layout .sidebar-nav-link--active {
  box-shadow: none;
  border-color: var(--crc-sidebar-active-border);
}

.sidebar-root--rail-layout .sidebar-nav-link--active::before {
  display: none;
}

.sidebar-root--rail-layout .sidebar-nav-link__icon {
  margin: 0;
  width: 1.125rem;
  height: 1.125rem;
  display: grid;
  place-items: center;
}

.sidebar-root--rail-layout .sidebar-nav-link__icon .pi {
  width: 1.125rem;
  height: 1.125rem;
  display: grid;
  place-items: center;
  text-align: center;
}

.sidebar-root--rail-layout .sidebar-user-card {
  width: var(--sidebar-rail-target-size);
  min-height: var(--sidebar-rail-target-size);
  margin-inline: 0;
  padding: 0;
  gap: 0;
  justify-content: center;
  align-items: center;
  border-color: transparent;
  background: transparent;
}

.sidebar-root--rail-layout .sidebar-user-card:hover {
  background: var(--crc-sidebar-link-hover-bg);
  border-color: transparent;
  border-radius: 0.6875rem;
}

.sidebar-root--rail-layout .sidebar-user-card__avatar {
  width: 2.375rem;
  height: 2.375rem;
  flex: 0 0 2.375rem;
}

.sidebar-root--rail-layout .sidebar-user-card__copy,
.sidebar-root--rail-layout .sidebar-user-card__menu-icon {
  display: none;
}

.sidebar-user-card-wrap {
  width: 100%;
  min-width: 0;
}

.sidebar-user-card {
  box-sizing: border-box;
  width: 100%;
  min-height: 3.625rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5625rem 0.625rem;
  border: 1px solid var(--crc-sidebar-line);
  border-radius: 0.75rem;
  background: var(--crc-sidebar-user-card-bg);
  color: var(--crc-sidebar-text);
  cursor: pointer;
  text-align: left;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.sidebar-user-card:hover {
  background: var(--crc-sidebar-user-card-hover-bg);
  border-color: var(--crc-sidebar-line-strong);
}

.sidebar-user-card:focus-visible {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 2px;
}

.sidebar-user-card__avatar {
  position: relative;
  width: 2.375rem;
  height: 2.375rem;
  flex: 0 0 2.375rem;
}

.sidebar-user-card__avatar-surface {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 0.6875rem;
  background: var(--crc-sidebar-avatar-bg);
  color: var(--crc-sidebar-avatar-text);
  font-size: 0.75rem;
  font-weight: 800;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-user-card__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sidebar-user-card__avatar::after {
  content: '';
  position: absolute;
  z-index: 1;
  right: -1px;
  bottom: -1px;
  width: 0.5625rem;
  height: 0.5625rem;
  border-radius: 999px;
  background: var(--crc-sidebar-active-icon);
  border: 2px solid var(--crc-sidebar-avatar-ring);
}

.sidebar-user-card__copy {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 10rem;
}

.sidebar-user-card__name {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-card__role {
  display: block;
  margin-top: 0.125rem;
  font-size: 0.625rem;
  color: var(--crc-sidebar-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-card__menu-icon {
  color: var(--crc-sidebar-text-muted);
  font-size: 0.875rem;
  flex: 0 0 auto;
  width: 0.875rem;
  max-width: 0.875rem;
}

.sidebar-root--hover-expanded .sidebar-nav-link,
.sidebar-root--hover-expanded .sidebar-user-card {
  width: 100%;
  margin-inline: 0;
}
</style>
